import React, { MouseEvent, ReactElement, useState } from 'react';
import { BASE_URL } from '../constants/routes';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Section } from './Section';
import { Container } from './Container';
import { Contact, ServiceOption } from './sections/Contact';
import { Button } from './buttons/Button';
import { ArrowDownIcon } from './icons/ArrowDownIcon';
import { Colors } from '../constants/colors';
import { Device } from '../constants/device';
import { usePageMeta } from '../helpers/usePageMeta';
import { unprefixedPath } from '../helpers/localizedPath';
import { showsNclp } from '../helpers/showsNclp';
import { ProjectSection } from '../model/ProjectSection';

const CONTACT_ANCHOR = 'contact';

type ListItem = {
  title: string;
  text: string;
};

type Quote = {
  text: string;
  source: string;
  url: string;
};

type Stat = {
  value: string;
  label: string;
};

type Service = {
  key: 'nclp' | 'audit' | 'sop' | 'complete';
  anchor: ProjectSection;
};

// Each service has its own section; the overview, the section CTAs and the
// service picker of the contact form all refer to it by this anchor.
const SERVICES: Service[] = [
  { key: 'nclp', anchor: 'ai-services-nclp' },
  { key: 'audit', anchor: 'ai-services-audit' },
  { key: 'sop', anchor: 'ai-services-sop' },
  { key: 'complete', anchor: 'ai-services-complete' },
];
const ALL_SERVICES = 'ai-services-all';

/**
 * Standalone page of the AI services for laboratories, linked from the
 * services menu in the header. The NČLP analysis is built around the Czech
 * national code list NČLP and Czech regulation, so only the Czech version
 * offers it, together with the complete laboratory audit that builds on it;
 * the other language versions offer the remaining services. Their texts
 * (aiServices.nclp, aiServices.complete and the matching
 * aiServices.overview.items) exist in cs.json only.
 */
export const AiServices = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const withNclp = showsNclp(i18n.language);
  const services = SERVICES.filter((service) => withNclp || (service.key !== 'nclp' && service.key !== 'complete'));
  // Sections alternate their background; without the three NČLP sections the
  // audit and SOP sections flip so that two shaded sections never meet. The
  // complete audit drops out too, so from the order section on both versions
  // match again.
  const shaded = (withNclpShaded: boolean) => withNclpShaded === withNclp;

  const canonical = `${BASE_URL}${unprefixedPath(pathname)}`;
  const title = t('aiServices.meta.title');
  const description = t('aiServices.meta.description');

  usePageMeta({
    title,
    description,
    canonical,
    ogTitle: title,
    ogDescription: description,
    ogUrl: canonical,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: t('aiServices.hero.title'),
      url: canonical,
      description,
      provider: {
        '@type': 'Organization',
        name: 'slp.blue',
        url: BASE_URL,
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: t('aiServices.hero.title'),
        itemListElement: services.map((service) => ({
          '@type': 'Offer',
          itemOffered: { '@type': 'Service', name: t(`aiServices.overview.items.${service.key}.title`) },
        })),
      },
    },
  });

  const listOf = <T,>(key: string) => t(key, { returnObjects: true }) as T[];
  const paragraphs = (key: string) => listOf<string>(key).map((paragraph, index) => <p key={index}>{paragraph}</p>);

  // Which service the visitor came from, so the enquiry says what it is about.
  const [service, setService] = useState('');
  const serviceOptions: ServiceOption[] = [
    ...services.map((item) => ({ value: item.anchor, label: t(`aiServices.overview.items.${item.key}.title`) })),
    { value: ALL_SERVICES, label: t('aiServices.contactForm.allServices') },
  ];

  // Falls back to the browser handling the href when the target is missing.
  const scrollToAnchor = (anchor: string) => (event: MouseEvent) => {
    const target = document.getElementById(anchor);
    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const callToAction = (label: string, selected?: string, inverse?: boolean) => (
    <CtaRow>
      <Button
        as="a"
        href={`#${CONTACT_ANCHOR}`}
        onClick={(event: MouseEvent) => {
          if (selected) {
            setService(selected);
          }
          scrollToAnchor(CONTACT_ANCHOR)(event);
        }}
        inverse={inverse}
        maxWidth={'420px'}
      >
        {label}
      </Button>
    </CtaRow>
  );

  const outputList = (key: string) => (
    <ItemList>
      {listOf<string>(key).map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ItemList>
  );

  return (
    <AiServicesMain>
      <Hero>
        <Container compact>
          <HeroLabel>{t('aiServices.hero.label')}</HeroLabel>
          <h1>{t('aiServices.hero.title')}</h1>
          <DividingLine />
          <HeroSubtitle>{t('aiServices.hero.subtitle')}</HeroSubtitle>
          {callToAction(t('aiServices.hero.cta'), undefined, true)}
        </Container>
      </Hero>

      <Section id={'ai-services-overview'} inverse heading={t('aiServices.overview.title')}>
        <Container compact>
          <Prose>
            <p>{t('aiServices.overview.intro')}</p>
          </Prose>
          <Cards>
            {services.map((item) => (
              <ServiceCard key={item.key} href={`#${item.anchor}`} onClick={scrollToAnchor(item.anchor)}>
                <CardTitle>{t(`aiServices.overview.items.${item.key}.title`)}</CardTitle>
                <CardText>{t(`aiServices.overview.items.${item.key}.text`)}</CardText>
                <CardMore>
                  {t('aiServices.overview.more')}
                  <ArrowDownIcon color={Colors.primary} size={18} strokeWidth={4} />
                </CardMore>
              </ServiceCard>
            ))}
          </Cards>
          <AssistantNote>
            <strong>{t('aiServices.overview.assistantTitle')}</strong>
            <p>{t('aiServices.overview.assistantText')}</p>
          </AssistantNote>
        </Container>
      </Section>

      {withNclp && (
        <>
          <Section id={'ai-services-nclp'} heading={t('aiServices.nclp.title')}>
            <Container compact>
              <Prose>
                <p>{t('aiServices.nclp.intro')}</p>
                <h3>{t('aiServices.nclp.whyTitle')}</h3>
                {paragraphs('aiServices.nclp.paragraphs')}
                <SourceNote>
                  <a href={t('aiServices.nclp.timelineUrl')} target="_blank" rel="noopener noreferrer">
                    {t('aiServices.nclp.timelineSource')}
                  </a>
                </SourceNote>
                <Quotes>
                  {listOf<Quote>('aiServices.nclp.quotes').map((quote, key) => (
                    <QuoteCard key={key}>
                      <blockquote>„{quote.text}“</blockquote>
                      <figcaption>
                        <a href={quote.url} target="_blank" rel="noopener noreferrer">
                          {quote.source}
                        </a>
                      </figcaption>
                    </QuoteCard>
                  ))}
                </Quotes>
                <h3>{t('aiServices.nclp.findingsTitle')}</h3>
                <ItemList>
                  {listOf<ListItem>('aiServices.nclp.findings').map((item, key) => (
                    <li key={key}>
                      <strong>{item.title}</strong>
                      {`: ${item.text}`}
                    </li>
                  ))}
                </ItemList>
              </Prose>
            </Container>
          </Section>

          <Section id={'ai-services-nclp-how'} inverse heading={t('aiServices.nclp.howTitle')}>
            <Container compact>
              <Steps>
                {listOf<ListItem>('aiServices.nclp.steps').map((step, key) => (
                  <Step key={key}>
                    <StepTitle>{step.title}</StepTitle>
                    <StepText>{step.text}</StepText>
                  </Step>
                ))}
              </Steps>
              <Prose>
                <h3>{t('aiServices.nclp.outputTitle')}</h3>
              </Prose>
              <Cards>
                {listOf<ListItem>('aiServices.nclp.outputs').map((item, key) => (
                  <Card key={key}>
                    <CardTitle>{item.title}</CardTitle>
                    <CardText>{item.text}</CardText>
                  </Card>
                ))}
              </Cards>
            </Container>
          </Section>

          <Section id={'ai-services-nclp-example'} heading={t('aiServices.nclp.exampleTitle')}>
            <Container compact>
              <Prose>
                <p>{t('aiServices.nclp.exampleIntro')}</p>
              </Prose>
              <Stats>
                {listOf<Stat>('aiServices.nclp.stats').map((stat, key) => (
                  <StatTile key={key}>
                    <StatValue>{stat.value}</StatValue>
                    <StatLabel>{stat.label}</StatLabel>
                  </StatTile>
                ))}
              </Stats>
              {callToAction(t('aiServices.nclp.cta'), 'ai-services-nclp')}
            </Container>
          </Section>
        </>
      )}

      <Section id={'ai-services-audit'} inverse={shaded(true)} heading={t('aiServices.audit.title')}>
        <Container compact>
          <Prose>
            {paragraphs('aiServices.audit.paragraphs')}
            <h3>{t('aiServices.audit.outputTitle')}</h3>
            {outputList('aiServices.audit.outputs')}
          </Prose>
          {callToAction(t('aiServices.audit.cta'), 'ai-services-audit')}
        </Container>
      </Section>

      <Section id={'ai-services-sop'} inverse={shaded(false)} heading={t('aiServices.sop.title')}>
        <Container compact>
          <Prose>
            {paragraphs('aiServices.sop.paragraphs')}
            <h3>{t('aiServices.sop.checksTitle')}</h3>
            {outputList('aiServices.sop.checks')}
            <h3>{t('aiServices.sop.outputTitle')}</h3>
            {outputList('aiServices.sop.outputs')}
          </Prose>
          {callToAction(t('aiServices.sop.cta'), 'ai-services-sop')}
        </Container>
      </Section>

      {withNclp && (
        <Section id={'ai-services-complete'} inverse heading={t('aiServices.complete.title')}>
          <Container compact>
            <Prose>
              {paragraphs('aiServices.complete.paragraphs')}
              <h3>{t('aiServices.complete.outputTitle')}</h3>
              {outputList('aiServices.complete.outputs')}
            </Prose>
            {callToAction(t('aiServices.complete.cta'), 'ai-services-complete')}
          </Container>
        </Section>
      )}

      <Section id={'ai-services-order'} heading={t('aiServices.order.title')}>
        <Container compact>
          <Steps>
            {listOf<ListItem>('aiServices.order.steps').map((step, key) => (
              <Step key={key}>
                <StepTitle>{step.title}</StepTitle>
                <StepText>{step.text}</StepText>
              </Step>
            ))}
          </Steps>
        </Container>
      </Section>

      <Section id={'ai-services-responsibility'} inverse heading={t('aiServices.responsibility.title')}>
        <Container compact>
          <Prose>{paragraphs('aiServices.responsibility.paragraphs')}</Prose>
        </Container>
      </Section>

      <Contact
        subtitleKey={'aiServices.contactForm.subtitle'}
        pageName={'slp.blue – AI služby'}
        serviceOptions={serviceOptions}
        serviceLabel={t('aiServices.contactForm.serviceLabel')}
        servicePlaceholder={t('aiServices.contactForm.servicePlaceholder')}
        service={service}
        onServiceChange={setService}
      />
    </AiServicesMain>
  );
};

const AiServicesMain = styled.main`
  a:focus-visible,
  button:focus-visible {
    outline: 3px solid ${Colors.primaryDark};
    outline-offset: 3px;
  }
`;

const Hero = styled.div`
  background: ${Colors.primary};
  color: ${Colors.white};
  padding: 80px 0 90px;

  ${Device.tablet} {
    padding: 40px 0 50px;
  }

  ${Container} {
    align-items: flex-start;
  }

  h1 {
    font-size: 42px;
    font-weight: 900;
    margin: 0 0 10px;
    max-width: 900px;

    ${Device.tablet} {
      font-size: 28px;
    }
  }
`;

const HeroLabel = styled.span`
  display: inline-flex;
  background: ${Colors.white};
  color: ${Colors.primary};
  border-radius: 20px;
  padding: 3px 14px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 15px;
`;

const DividingLine = styled.div`
  background: ${Colors.primaryLight};
  height: 5px;
  width: 100px;
  margin-bottom: 25px;
`;

const HeroSubtitle = styled.p`
  font-size: 22px;
  font-weight: 500;
  line-height: 1.6;
  max-width: 780px;
  margin: 0;

  ${Device.tablet} {
    font-size: 18px;
  }
`;

const CtaRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 35px;

  a {
    text-decoration: none;
  }
`;

const Prose = styled.div`
  /* Section centres its paragraphs; long form copy and lists read better left aligned. */
  p {
    text-align: left !important;
  }

  h3 {
    font-size: 22px;
    margin: 40px 0 10px;

    ${Device.tablet} {
      font-size: 19px;
    }
  }
`;

const SourceNote = styled.p`
  margin: -5px 0 0;
  font-size: 13px;

  a {
    color: ${Colors.darkGrey};
  }
`;

const Quotes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 30px;
`;

const QuoteCard = styled.figure`
  margin: 0;
  padding: 20px 25px;
  border-left: 5px solid ${Colors.primary};
  border-radius: 0 20px 20px 0;
  background: ${Colors.white};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  blockquote {
    margin: 0 0 8px;
    font-size: 17px;
    font-weight: 500;
    line-height: 1.6;
    color: ${Colors.primaryDark};
  }

  figcaption {
    font-size: 13px;
  }

  figcaption a {
    color: ${Colors.darkGrey};
  }
`;

const ItemList = styled.ul`
  line-height: 2;
  padding-left: 20px;
  margin: 0;

  li {
    margin-bottom: 8px;
  }
`;

// However many steps there are, they share the full width and wrap when narrow.
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  list-style: none;
  counter-reset: step;
  margin: 0;
  padding: 0;

  ${Device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.li`
  counter-increment: step;
  background: ${Colors.white};
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  &:before {
    content: counter(step);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${Colors.primary};
    color: ${Colors.white};
    font-weight: 700;
    margin-bottom: 15px;
  }
`;

const StepTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 8px;
`;

const StepText = styled.p`
  margin: 0;
  /* Overrides the centred, double spaced paragraph styling of Section. */
  text-align: left !important;
  line-height: 1.7 !important;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 25px;
  margin-top: 30px;
`;

const AssistantNote = styled.div`
  margin-top: 30px;
  padding: 20px 25px;
  border-radius: 20px;
  border: 2px solid ${Colors.primaryLight};

  strong {
    color: ${Colors.primary};
  }

  p {
    margin: 8px 0 0;
    /* Overrides the centred, double spaced paragraph styling of Section. */
    text-align: left !important;
    line-height: 1.7 !important;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: ${Colors.white};
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 8px;
  color: ${Colors.primary};
`;

const CardText = styled.p`
  margin: 0;
  /* Overrides the centred, double spaced paragraph styling of Section. */
  text-align: left !important;
  line-height: 1.7 !important;
`;

const ServiceCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: ${Colors.white};
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: inherit;
  text-decoration: none;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
    transform: translateY(-2px);
  }
`;

const CardMore = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 15px;
  color: ${Colors.primary};
  font-weight: 700;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin: 30px 0 10px;

  ${Device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  background: ${Colors.white};
  border-radius: 20px;
  padding: 25px 15px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.span`
  font-size: 40px;
  font-weight: 900;
  line-height: 1.1;
  color: ${Colors.primary};

  ${Device.tablet} {
    font-size: 32px;
  }
`;

const StatLabel = styled.span`
  font-size: 15px;
  line-height: 1.4;
`;
